import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import useDarkMode from "../../../hooks/useDarkMode";
import moment from "moment";
import useThemeColor from "../../../hooks/useThemeColor";
import "./MUITimePicker.css";

const ITEM_HEIGHT = 34;

function WheelColumn({ items, value, onChange, ariaLabel, formatItem }) {
  const listRef = useRef(null);
  const suppressScrollRef = useRef(false);

  const scrollToValue = useCallback(
    (val, behavior = "auto") => {
      const idx = items.indexOf(val);
      if (idx < 0 || !listRef.current) return;
      suppressScrollRef.current = true;
      listRef.current.scrollTo({
        top: idx * ITEM_HEIGHT,
        behavior,
      });
      window.setTimeout(() => {
        suppressScrollRef.current = false;
      }, behavior === "smooth" ? 220 : 40);
    },
    [items]
  );

  useEffect(() => {
    scrollToValue(value, "auto");
  }, [value, scrollToValue]);

  const handleScroll = () => {
    if (!listRef.current || suppressScrollRef.current) return;
    const idx = Math.round(listRef.current.scrollTop / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(items.length - 1, idx));
    const next = items[clamped];
    if (next !== value) onChange(next);
  };

  const handleScrollEnd = () => {
    if (!listRef.current) return;
    const idx = Math.round(listRef.current.scrollTop / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(items.length - 1, idx));
    scrollToValue(items[clamped], "smooth");
    onChange(items[clamped]);
  };

  return (
    <div className="mui-time-wheel" aria-label={ariaLabel}>
      <div className="mui-time-wheel__fade mui-time-wheel__fade--top" aria-hidden="true" />
      <div className="mui-time-wheel__fade mui-time-wheel__fade--bottom" aria-hidden="true" />
      <div className="mui-time-wheel__highlight" aria-hidden="true" />
      <div
        ref={listRef}
        className="mui-time-wheel__list"
        onScroll={handleScroll}
        onMouseUp={handleScrollEnd}
        onTouchEnd={handleScrollEnd}
        role="listbox"
        tabIndex={0}
        onKeyDown={(e) => {
          const idx = items.indexOf(value);
          if (e.key === "ArrowDown" && idx < items.length - 1) {
            e.preventDefault();
            onChange(items[idx + 1]);
            scrollToValue(items[idx + 1], "smooth");
          }
          if (e.key === "ArrowUp" && idx > 0) {
            e.preventDefault();
            onChange(items[idx - 1]);
            scrollToValue(items[idx - 1], "smooth");
          }
        }}
      >
        <div style={{ height: ITEM_HEIGHT * 2 }} aria-hidden="true" />
        {items.map((item) => {
          const selected = item === value;
          return (
            <button
              key={String(item)}
              type="button"
              role="option"
              aria-selected={selected}
              className={`mui-time-wheel__item${selected ? " is-selected" : ""}`}
              onClick={() => {
                onChange(item);
                scrollToValue(item, "smooth");
              }}
            >
              {formatItem ? formatItem(item) : String(item).padStart(2, "0")}
            </button>
          );
        })}
        <div style={{ height: ITEM_HEIGHT * 2 }} aria-hidden="true" />
      </div>
    </div>
  );
}

const MUITimePicker = ({
  label,
  value,
  onChange,
  placeholder = "Select time",
  name = "",
  disabled = false,
  clearable = false,
  helperText = "",
  error = false,
  className = "",
  style = {},
  ampm = false,
  format24 = false,
  minuteStep = 1,
  preferDropUp = false,
  ...props
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const getHourMinutePeriod = useCallback((m, useAmpm) => {
    if (!m || !moment.isMoment(m)) return null;
    const hour24 = m.hour();
    const minute = m.minute();
    if (useAmpm) {
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      return { hour: hour12, minute, isPM: hour24 >= 12 };
    }
    return { hour: hour24, minute, isPM: hour24 >= 12 };
  }, []);

  const parseIncomingTime = (incoming) => {
    if (!incoming) return null;
    if (moment.isMoment(incoming)) return incoming.isValid() ? incoming : null;
    const formats = ["HH:mm:ss", "HH:mm", "h:mm A", "h:mmA", moment.ISO_8601];
    const strict = moment(String(incoming).trim(), formats, true);
    if (strict.isValid()) return strict;
    const loose = moment(String(incoming).trim());
    return loose.isValid() ? loose : null;
  };

  const initialTime = parseIncomingTime(value);
  const initialHMP = initialTime ? getHourMinutePeriod(initialTime, ampm) : null;
  const now = moment();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(initialTime);
  const [currentHour, setCurrentHour] = useState(
    initialHMP
      ? initialHMP.hour
      : ampm
        ? now.hour() === 0
          ? 12
          : now.hour() > 12
            ? now.hour() - 12
            : now.hour()
        : now.hour()
  );
  const [currentMinute, setCurrentMinute] = useState(
    initialHMP ? initialHMP.minute : now.minute()
  );
  const [isPM, setIsPM] = useState(initialHMP ? initialHMP.isPM : now.hour() >= 12);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const time = parseIncomingTime(value);
    if (time) {
      setSelectedTime(time);
      const hmp = getHourMinutePeriod(time, ampm);
      if (hmp) {
        setCurrentHour(hmp.hour);
        setCurrentMinute(hmp.minute);
        setIsPM(hmp.isPM);
      }
    } else {
      setSelectedTime(null);
    }
    // Compare by formatted clock time so new moment instances with the same HH:mm do not loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value && moment.isMoment(value)
      ? value.format("HH:mm")
      : value
        ? String(value)
        : "",
    ampm,
    getHourMinutePeriod,
  ]);

  const updatePopupPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupWidth = 260;
    const popupHeight = 280;
    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - popupWidth - 8));

    let top;
    if (preferDropUp || rect.bottom + popupHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupHeight - 8);
    } else {
      top = rect.bottom + 8;
    }
    setPopupPos({ top, left });
  }, [preferDropUp]);

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePopupPosition();

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !event.target.closest?.(".mui-time-picker-popup")
      ) {
        setIsOpen(false);
      }
    };

    const handleViewport = () => updatePopupPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleViewport);
    window.addEventListener("scroll", handleViewport, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleViewport);
      window.removeEventListener("scroll", handleViewport, true);
    };
  }, [isOpen, updatePopupPosition]);

  const hours = useMemo(() => {
    const list = [];
    const maxHour = ampm ? 12 : 23;
    const startHour = ampm ? 1 : 0;
    for (let i = startHour; i <= maxHour; i++) list.push(i);
    return list;
  }, [ampm]);

  const minutes = useMemo(() => {
    const list = [];
    for (let i = 0; i < 60; i += minuteStep) list.push(i);
    return list;
  }, [minuteStep]);

  const periods = useMemo(() => ["AM", "PM"], []);

  const toggleTimePicker = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    if (!isOpen && selectedTime) {
      const hmp = getHourMinutePeriod(selectedTime, ampm);
      if (hmp) {
        setCurrentHour(hmp.hour);
        setCurrentMinute(hmp.minute);
        setIsPM(hmp.isPM);
      }
    }
    setIsOpen((prev) => !prev);
  };

  const buildMoment = useCallback(() => {
    let hour = currentHour;
    if (ampm) {
      if (isPM && hour !== 12) hour += 12;
      else if (!isPM && hour === 12) hour = 0;
    }
    return moment().hour(hour).minute(currentMinute).second(0);
  }, [ampm, currentHour, currentMinute, isPM]);

  const handleTimeSelect = () => {
    const newTime = buildMoment();
    setSelectedTime(newTime);
    onChange?.(newTime);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedTime(null);
    onChange?.(null);
    setIsOpen(false);
  };

  const formatDisplayTime = () => {
    if (!selectedTime) return "";
    return ampm || !format24 ? selectedTime.format("h:mm A") : selectedTime.format("HH:mm");
  };

  const previewLabel = useMemo(() => {
    const m = buildMoment();
    return ampm ? m.format("h:mm A") : m.format("HH:mm");
  }, [ampm, buildMoment]);

  const popup = isOpen
    ? createPortal(
        <div
          className={`mui-time-picker-popup ${isDarkMode ? "dark" : "light"}`}
          style={{
            "--theme-color": themeColor,
            position: "fixed",
            top: popupPos.top,
            left: popupPos.left,
            zIndex: 20050,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Select time"
        >
          <div className="mui-time-picker-popup__preview">
            <span className="mui-time-picker-popup__preview-label">Selected</span>
            <span className="mui-time-picker-popup__preview-value">{previewLabel}</span>
          </div>

          <div className={`mui-time-wheels${ampm ? " mui-time-wheels--ampm" : ""}`}>
            <div className="mui-time-wheel-col">
              <span className="mui-time-wheel-col__label">Hour</span>
              <WheelColumn
                items={hours}
                value={currentHour}
                onChange={setCurrentHour}
                ariaLabel="Hour"
              />
            </div>
            <span className="mui-time-wheels__sep" aria-hidden="true">
              :
            </span>
            <div className="mui-time-wheel-col">
              <span className="mui-time-wheel-col__label">Min</span>
              <WheelColumn
                items={minutes}
                value={currentMinute}
                onChange={setCurrentMinute}
                ariaLabel="Minute"
              />
            </div>
            {ampm && (
              <div className="mui-time-wheel-col">
                <span className="mui-time-wheel-col__label">Period</span>
                <WheelColumn
                  items={periods}
                  value={isPM ? "PM" : "AM"}
                  onChange={(p) => setIsPM(p === "PM")}
                  ariaLabel="Period"
                  formatItem={(p) => p}
                />
              </div>
            )}
          </div>

          <div className="mui-time-picker-popup__actions">
            {clearable && (
              <button type="button" className="mui-time-picker-btn mui-time-picker-btn--ghost" onClick={handleClear}>
                Clear
              </button>
            )}
            <button
              type="button"
              className="mui-time-picker-btn mui-time-picker-btn--ghost"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
            <button type="button" className="mui-time-picker-btn mui-time-picker-btn--primary" onClick={handleTimeSelect}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={containerRef}
      className={`mui-time-picker-container ${isDarkMode ? "dark" : "light"} ${className}`}
      style={{ "--theme-color": themeColor, position: "relative", ...style }}
    >
      {label && (
        <label
          className={`mui-label${error ? " error" : ""}`}
          htmlFor={`time-input-${name}`}
        >
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        className={`mui-time-picker-trigger${isOpen ? " is-open" : ""}${disabled ? " is-disabled" : ""}`}
        onClick={toggleTimePicker}
      >
        <input
          id={`time-input-${name}`}
          type="text"
          value={formatDisplayTime()}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          name={name}
          className="mui-time-picker-trigger__input"
          {...props}
        />
        <span className="mui-time-picker-trigger__icon" aria-hidden="true">
          <i className="fa fa-clock" />
        </span>
      </div>

      {helperText && (
        <div className={`mui-helper-text${error ? " error" : ""}`}>{helperText}</div>
      )}

      {popup}
    </div>
  );
};

export default MUITimePicker;
